import { configureI18n, I18n } from "@assets/strings";
import { Environment } from "@core/config/environment";
import { useServices } from "@core/servicesContext";
import {
	DataStepId,
	missingCompleteProfileSteps,
	showsCompleteProfileThanks,
	StepId,
} from "@domain/currentProfile/completeProfilePrompt";
import { CountryIndicator } from "@domain/currentProfile/countriesIndicators";
import { allCountrycodes, countries, Country, countryCodes } from "@domain/currentProfile/countryCode";
import { allLanguages, CurrentProfile, Language, UserGender } from "@domain/currentProfile/currentProfile";
import { useCurrentProfile } from "@domain/currentProfile/hooks";
import { useSuggestedProfiles } from "@domain/user/hooks";
import { fullName } from "@domain/user/utils";
import { Button } from "@ui/components/buttons/button";
import { CategoriesSelector } from "@ui/components/category/categoriesSelector";
import { Tertiary } from "@ui/components/containers/textBlocks";
import { CountryDatalist } from "@ui/components/inputs/countryDatalist";
import { DatePicker } from "@ui/components/inputs/datePicker";
import { IndicatorDataList } from "@ui/components/inputs/indicatorDataList";
import { SimpleTextInput } from "@ui/components/inputs/simpleTextInput";
import { FollowButton } from "@ui/components/profile/followButton";
import { ProfilePicture } from "@ui/components/profile/profilePicture";
import { ClosableModal } from "@ui/modals/closableModal";
import { CloseButton } from "@ui/modals/closeButton";
import { createToast } from "@ui/toasts/utils";
import { Set } from "immutable";
import React, { useLayoutEffect, useRef, useState } from "react";
import { ClipLoader } from "react-spinners";
import styled from "styled-components";

type GenderChoice = UserGender | "OTHER";

const GENDER_ICONS: Record<GenderChoice, React.ReactNode> = {
	FEMALE: (
		<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
			<circle cx="12" cy="8.5" r="5" />
			<path d="M12 13.5v7M9 18h6" />
		</svg>
	),
	MALE: (
		<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
			<circle cx="10" cy="14" r="5" />
			<path d="M14.5 9.5l5-5M15 4.5h4.5V9" />
		</svg>
	),
	OTHER: (
		<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
			<circle cx="12" cy="12" r="4" />
			<path d="M12 8V3M9.5 5h5M12 16v5M9.5 19h5" />
		</svg>
	),
};

interface CompleteProfileModalProps {
	close: () => void;
}

function stepsFor(profile: CurrentProfile): StepId[] {
	const steps = missingCompleteProfileSteps(profile);
	if (steps.length === 0) {
		return [];
	}
	return [...steps, "thanks"];
}

const STEP_TEXT: Record<Exclude<StepId, "thanks">, { title: string; lead: string }> = {
	phone: { title: "profile.complete.phone.title", lead: "profile.complete.phone.lead" },
	language: { title: "profile.complete.language.title", lead: "profile.complete.language.lead" },
	country: { title: "profile.complete.country.title", lead: "profile.complete.country.lead" },
	birthDate: { title: "profile.complete.birth.title", lead: "profile.complete.birth.lead" },
	gender: { title: "profile.complete.gender.title", lead: "profile.complete.gender.lead" },
	interests: { title: "profile.complete.interests.title", lead: "profile.complete.interests.lead" },
	follows: { title: "profile.complete.follows.title", lead: "profile.complete.follows.lead" },
};

function minimumAge(): number {
	return Environment.APP_VARIANT === "jaangal" ? 9 : 12;
}

function isAllowedBirthDate(date: Date | undefined): date is Date {
	if (!date || Number.isNaN(date.getTime())) {
		return false;
	}
	const year = date.getFullYear();
	const currentYear = new Date().getFullYear();
	return year >= currentYear - 100 && year <= currentYear - minimumAge();
}

export const CompleteProfileModal: React.FC<CompleteProfileModalProps> = ({ close }) => {
	const profile = useCurrentProfile();
	const { currentProfileService } = useServices();
	const [steps] = useState<StepId[]>(() => (profile ? stepsFor(profile) : []));
	const [index, setIndex] = useState(0);
	const [saving, setSaving] = useState(false);
	const [indicator, setIndicator] = useState<CountryIndicator>("221");
	const [phone, setPhone] = useState("");
	const [phoneError, setPhoneError] = useState<"empty" | "invalid" | null>(null);
	const [language, setLanguage] = useState<Language | undefined>(profile?.language);
	const [country, setCountry] = useState<Country | undefined>(
		profile?.countryCode ? countries[profile.countryCode] : undefined
	);
	const [birthDate, setBirthDate] = useState<Date | undefined>(profile?.birthDate);
	const [gender, setGender] = useState<GenderChoice | null>(
		profile?.gender ?? (profile?.customData?.askedGender ? "OTHER" : null)
	);
	const [categories, setCategories] = useState(() => Set(profile?.categories ?? []));
	const [completedSteps, setCompletedSteps] = useState(() => Set<DataStepId>());
	const [hasFollow, setHasFollow] = useState(false);
	const step = steps[Math.min(index, steps.length - 1)];
	const isThanks = step === "thanks";

	useLayoutEffect(() => {
		if (!profile || steps.length === 0) {
			close();
		}
	}, [close, profile, steps.length]);
	const dataSteps = steps.filter((id): id is DataStepId => id !== "thanks");
	const isLastDataStep = index === dataSteps.length - 1;

	if (!profile || steps.length === 0) {
		return null;
	}

	const leaveStep = (submitted: boolean): void => {
		if (step === "thanks") {
			close();
			return;
		}
		const stepDone = step === "follows" ? hasFollow : submitted;
		const nextCompleted =
			step === "follows"
				? stepDone
					? completedSteps.add(step)
					: completedSteps.remove(step)
				: submitted
					? completedSteps.add(step)
					: completedSteps;
		setCompletedSteps(nextCompleted);
		if (index < dataSteps.length - 1) {
			setIndex((current) => current + 1);
			return;
		}
		if (showsCompleteProfileThanks(dataSteps, nextCompleted)) {
			setIndex(dataSteps.length);
			return;
		}
		close();
	};

	const onNext = async (): Promise<void> => {
		if (saving) {
			return;
		}
		if (step === "thanks") {
			close();
			return;
		}
		if (step === "phone") {
			const digits = phone.replace(/\D/g, "");
			if (digits.length < 9) {
				setPhoneError(digits.length === 0 ? "empty" : "invalid");
				return;
			}
		}
		if (step === "country" && country && !(country in countryCodes)) {
			return;
		}
		if (step === "birthDate" && birthDate && !isAllowedBirthDate(birthDate)) {
			return;
		}
		setSaving(true);
		try {
			if (step === "phone") {
				await currentProfileService.saveProfileInfos({ phoneNumberAuth: `+${indicator}${phone.replace(/\D/g, "")}` });
			} else if (step === "language" && language) {
				await currentProfileService.saveProfileInfos({ language });
				configureI18n(language === "FRENCH" ? "fr" : "en");
			} else if (step === "country" && country && country in countryCodes) {
				await currentProfileService.saveProfileInfos({ countryCode: countryCodes[country] });
			} else if (step === "birthDate" && isAllowedBirthDate(birthDate)) {
				await currentProfileService.saveProfileInfos({ birthDate });
			} else if (step === "gender" && gender) {
				await currentProfileService.saveGenderChoice(gender);
			} else if (step === "interests" && categories.size > 0) {
				await currentProfileService.saveProfileInfos({ categories: categories.toArray() });
			}
			const submitted =
				step === "phone" ||
				(step === "language" && !!language) ||
				(step === "country" && !!country && country in countryCodes) ||
				(step === "birthDate" && isAllowedBirthDate(birthDate)) ||
				(step === "gender" && gender !== null) ||
				(step === "interests" && categories.size > 0);
			leaveStep(submitted);
		} catch {
			createToast(I18n.t("error"), I18n.t("profile.save.error"), "error");
		} finally {
			setSaving(false);
		}
	};

	const text = step === "thanks" ? undefined : STEP_TEXT[step];

	return (
		<Container close={close} keyboardEventDisabled={saving}>
			{index > 0 && !isThanks && (
				<NavButton
					type="button"
					aria-label={I18n.t("back")}
					disabled={saving}
					onClick={() => setIndex((current) => current - 1)}
				>
					<Chevron />
				</NavButton>
			)}
			<Dismiss type="button" aria-label={I18n.t("close")} disabled={saving} onClick={close}>
				×
			</Dismiss>
			{!isThanks && (
				<Dots>
					{steps.slice(0, -1).map((id, dotIndex) => (
						<Dot key={id} $active={dotIndex === index} />
					))}
				</Dots>
			)}
			{text && (
				<>
					<Title>{I18n.t(text.title)}</Title>
					<Lead>{I18n.t(text.lead)}</Lead>
				</>
			)}
			<Body>
				{step === "phone" && (
					<>
						<PhoneRow>
							<IndicatorDataList value={indicator} onChange={setIndicator} />
							<SimpleTextInput
								value={phone}
								type="tel"
								autoComplete="tel-national"
								placeholder={I18n.t("profile.complete.phone.placeholder")}
								error={phoneError ? I18n.t(`profile.complete.phone.${phoneError}`) : undefined}
								onChange={(value) => {
									setPhone(value.replace(/\D/g, "").slice(0, 10));
									setPhoneError(null);
								}}
							/>
						</PhoneRow>
					</>
				)}
				{step === "language" && (
					<LanguageChoices>
						{allLanguages.map((lang) => (
							<Choice key={lang} type="button" $selected={language === lang} onClick={() => setLanguage(lang)}>
								{I18n.t(`onboarding.language.${lang}`)}
							</Choice>
						))}
					</LanguageChoices>
				)}
				{step === "country" && (
					<CountryField>
						<CountryDatalist
							id="complete-profile-country"
							value={country}
							onChange={setCountry}
							placeholder={I18n.t("onboarding.country.placeholder")}
							options={allCountrycodes}
						/>
					</CountryField>
				)}
				{step === "birthDate" && (
					<DatePicker
						value={birthDate}
						onChange={(date) => setBirthDate(new Date(date.getFullYear(), 0, 1))}
						minYear={new Date().getFullYear() - 100}
						maxYear={new Date().getFullYear() - minimumAge()}
						hideMonth
					/>
				)}
				{step === "gender" && (
					<Choices>
						{(["FEMALE", "MALE", "OTHER"] as const).map((choice) => (
							<Choice key={choice} type="button" $selected={gender === choice} onClick={() => setGender(choice)}>
								<ChoiceIcon $selected={gender === choice} aria-hidden="true">
									{GENDER_ICONS[choice]}
								</ChoiceIcon>
								{I18n.t(
									choice === "OTHER" ? "profile.complete.gender.other" : `onboarding.gender.${choice.toLowerCase()}`
								)}
							</Choice>
						))}
					</Choices>
				)}
				{step === "interests" && (
					<Interests>
						<CategoriesSelector selected={categories} onSelectionChange={setCategories} />
					</Interests>
				)}
				{step === "follows" && <FollowSuggestions onFollowedChange={setHasFollow} />}
				{step === "thanks" && (
					<Thanks>
						<Check>
							<svg width="34" height="34" viewBox="0 0 24 24" fill="none" aria-hidden="true">
								<path
									d="M5 12l4 4 10-10"
									stroke="currentColor"
									strokeWidth="2.4"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
							</svg>
						</Check>
						<Title>{I18n.t("profile.complete.thanks.title")}</Title>
						<Lead>{I18n.t("profile.complete.thanks.lead")}</Lead>
					</Thanks>
				)}
			</Body>
			<Actions>
				{!isThanks && (
					<Skip
						type="button"
						disabled={saving}
						onClick={() => {
							setPhoneError(null);
							leaveStep(false);
						}}
					>
						{I18n.t("profile.complete.skip")}
					</Skip>
				)}
				<Button variant="primary" size="md" block loading={saving} onClick={() => void onNext()}>
					{isThanks
						? I18n.t("profile.complete.thanks.action")
						: isLastDataStep
							? I18n.t("profile.complete.finish")
							: I18n.t("auth.signup.continue")}
				</Button>
			</Actions>
		</Container>
	);
};

const FollowSuggestions: React.FC<{ onFollowedChange: (followed: boolean) => void }> = ({ onFollowedChange }) => {
	const { result, loading } = useSuggestedProfiles(8);
	const suggestions = (result ?? []).slice(0, 5);
	const followedIds = useRef(Set<string>());

	if (loading) {
		return (
			<LoaderWrap>
				<ClipLoader size={28} />
			</LoaderWrap>
		);
	}
	if (suggestions.length === 0) {
		return <Tertiary>{I18n.t("profile.suggest.empty")}</Tertiary>;
	}
	return (
		<FollowList>
			{suggestions.map((suggested) => (
				<FollowRow key={suggested.id}>
					<ProfilePicture picture={suggested.avatar} size={44} alt={fullName(suggested)} />
					<FollowName>
						<b>{fullName(suggested)}</b>
						{!!suggested.description && <span>{suggested.description}</span>}
					</FollowName>
					<FollowButton
						profile={suggested}
						onToggle={() => {
							const current = followedIds.current;
							const next = current.has(suggested.id) ? current.remove(suggested.id) : current.add(suggested.id);
							followedIds.current = next;
							onFollowedChange(next.size > 0);
						}}
					/>
				</FollowRow>
			))}
		</FollowList>
	);
};

const Container = styled(ClosableModal)`
	&& {
		max-width: 440px;
		max-height: min(640px, 86vh);
		overflow: visible;
		margin: 0;
		padding: 26px 24px 22px;
		border: none;
		border-radius: 28px;
	}

	${CloseButton} {
		display: none;
	}
`;

const NavButton = styled.button`
	position: absolute;
	top: 16px;
	left: 16px;
	width: 34px;
	height: 34px;
	border: none;
	border-radius: 50%;
	background: ${({ theme }) => theme.colors.simplegray};
	color: ${({ theme }) => theme.colors.textSecondary};
	display: flex;
	align-items: center;
	justify-content: center;
	cursor: pointer;
	padding: 0;

	&:hover:not(:disabled) {
		background: ${({ theme }) => theme.colors.primaryLight};
		color: ${({ theme }) => theme.colors.primary};
	}

	&:disabled {
		opacity: 0.35;
		cursor: not-allowed;
	}
`;

const Dismiss = styled.button`
	position: absolute;
	top: 16px;
	right: 16px;
	width: 34px;
	height: 34px;
	border: none;
	border-radius: 50%;
	background: ${({ theme }) => theme.colors.simplegray};
	color: ${({ theme }) => theme.colors.textSecondary};
	display: flex;
	align-items: center;
	justify-content: center;
	cursor: pointer;
	padding: 0;
	font-size: 20px;
	line-height: 1;
	font-family: inherit;

	&:hover:not(:disabled) {
		background: ${({ theme }) => theme.colors.primaryLight};
		color: ${({ theme }) => theme.colors.primary};
	}

	&:disabled {
		opacity: 0.35;
		cursor: not-allowed;
	}
`;

const Chevron: React.FC = () => (
	<svg
		width="18"
		height="18"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2.2"
		strokeLinecap="round"
		strokeLinejoin="round"
		aria-hidden="true"
	>
		<path d="M15 18l-6-6 6-6" />
	</svg>
);

const Dots = styled.div`
	display: flex;
	justify-content: center;
	gap: 6px;
	margin: 2px 0 22px;
`;

const Dot = styled.span<{ $active: boolean }>`
	height: 7px;
	border-radius: 4px;
	background: ${({ theme, $active }) => ($active ? theme.colors.primary : theme.colors.lightergray)};
	width: ${({ $active }) => ($active ? 20 : 7)}px;
`;

const Title = styled.h2`
	margin: 0;
	font-size: 1.3rem;
	font-weight: 700;
	color: ${({ theme }) => theme.colors.textPrimary};
`;

const Lead = styled.p`
	margin: 6px 0 0;
	color: ${({ theme }) => theme.colors.textSecondary};
	font-size: 0.9rem;
	line-height: 1.45;
`;

const Body = styled.div`
	margin: 20px 0 22px;
`;

const Interests = styled.div`
	max-height: 280px;
	overflow: auto;
`;

const PhoneRow = styled.div`
	display: grid;
	grid-template-columns: 110px minmax(0, 1fr);
	gap: 8px;
	align-items: start;
`;

const Choices = styled.div`
	display: grid;
	grid-template-columns: repeat(3, minmax(0, 1fr));
	gap: 10px;
`;

const LanguageChoices = styled(Choices)`
	grid-template-columns: repeat(2, minmax(0, 1fr));
`;

const CountryField = styled.div`
	position: relative;
`;

const Choice = styled.button<{ $selected: boolean }>`
	cursor: pointer;
	display: flex;
	flex-direction: column;
	align-items: center;
	border-radius: 16px;
	border: 1.5px solid ${({ theme, $selected }) => ($selected ? theme.colors.primary : theme.colors.lightergray)};
	background: ${({ theme, $selected }) => ($selected ? theme.colors.primaryLight : theme.colors.white)};
	color: ${({ theme }) => theme.colors.textPrimary};
	font-weight: 600;
	font-size: 0.875rem;
	padding: 16px 6px;
	font-family: inherit;
	text-align: center;
`;

const ChoiceIcon = styled.span<{ $selected: boolean }>`
	width: 40px;
	height: 40px;
	margin: 0 auto 8px;
	border-radius: 12px;
	display: flex;
	align-items: center;
	justify-content: center;
	background: ${({ theme, $selected }) => ($selected ? theme.colors.primary : theme.colors.primaryLight)};
	color: ${({ theme, $selected }) => ($selected ? theme.colors.white : theme.colors.primary)};
`;

const Actions = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;

	> button:last-child {
		flex: 1;
	}
`;

const Skip = styled.button`
	border: none;
	background: none;
	cursor: pointer;
	color: ${({ theme }) => theme.colors.textSecondary};
	font-weight: 600;
	font-size: 0.875rem;
	padding: 12px 10px;
	font-family: inherit;

	&:disabled {
		opacity: 0.5;
		cursor: not-allowed;
	}
`;

const FollowList = styled.div`
	display: flex;
	flex-direction: column;
	gap: 12px;
`;

const FollowRow = styled.div`
	display: flex;
	align-items: center;
	gap: 12px;
`;

const FollowName = styled.div`
	flex: 1;
	min-width: 0;

	b,
	span {
		display: block;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	b {
		font-size: 0.95rem;
		color: ${({ theme }) => theme.colors.textPrimary};
	}

	span {
		font-size: 0.8125rem;
		color: ${({ theme }) => theme.colors.textSecondary};
	}
`;

const LoaderWrap = styled.div`
	display: flex;
	justify-content: center;
	padding: 24px 0;
`;

const Thanks = styled.div`
	text-align: center;
	padding: 8px 0 4px;
`;

const Check = styled.div`
	width: 76px;
	height: 76px;
	margin: 0 auto 18px;
	border-radius: 50%;
	display: flex;
	align-items: center;
	justify-content: center;
	background: ${({ theme }) => theme.colors.primaryLight};
	color: ${({ theme }) => theme.colors.primary};
`;
